<template>
  <bulk-update-wrapper
    :saving="isSaving"
    title="Add to Project"
    :button-label="props.buttonLabel"
    :has-changes="hasChanges"
    :show-hint="showHint"
    @dismiss="dismiss"
    @project-selected="onProjectSelection"
    @save="onUserConfirmation"
  >
    <project-selection-form
      :job-ids="selectedJobIds"
      :require-user-confirmation="requireUserConfirmation"
      :is-bulk-update="true"
      @dismiss="onProjectFormDismissed"
      @job-added-to-project="onJobsAddedtoProject"
      @project-selected="onProjectSelection"
      @project-created="onProjectCreation"
      @project-loading-error="onProjectLoadingError"
    />

    <template #error-snackbar>
      <vs-snackbar
        v-if="projectStatusMessage"
        v-model="hasProjectStatusMessage"
        sticky
        :type="projectStatusMessage.type"
        @input="clearStatusMessage"
      >
        {{ projectStatusMessage.message }}
      </vs-snackbar>
      <vs-snackbar
        v-if="jobStatusMessage"
        v-model="hasJobStatusMessage"
        sticky
        :type="jobStatusMessage.type"
        @input="clearStatusMessage"
      >
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import { JobJson } from '@/models/types/job-json'
import { computed, ref, onMounted, provide, watchEffect } from 'vue'
import BulkUpdateWrapper from '@/components/common/BulkUpdateWrapper.vue'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import ProjectSelectionForm from '@/components/project/ProjectSelectionForm.vue'
import { ProjectSelectionMessage } from '../project/ProjectSelectionForm.vue'

// ---------------------------
//  Props
// ---------------------------

const props = defineProps<{
  buttonLabel: string
  selectedJobs: JobJson[]
}>()

// ---------------------------
//  Events
// ---------------------------

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

// ---------------------------
//  Refs
// ---------------------------

const initialProjectId = ref<string | null>(null)
const selectedProjectId = ref<string | null | undefined>()

// Indicators
const isSaving = ref(false)

// User
const userConfirmed = ref(false)
const requireUserConfirmation = ref(true)

// Snackbar Messaging
const hasProjectLoadingError = ref(false)
const projectStatusMessage = ref(null)
const hasProjectStatusMessage = ref(false)
const jobStatusMessage = ref<BulkUpdateResponse | null>(null)
const hasJobStatusMessage = ref(false)

// ---------------------------
//  Lifecycle Hooks
// ---------------------------

onMounted(() => {
  // Check to see if any of the selected jobs are currently assigned
  // to any projects to set the initial project id so determine if
  // the form has changes.
  const selectedProjectIds: Set<string | null> = new Set(
    props.selectedJobs.map((job) => job.project_id),
  )

  if (selectedProjectIds.size === 1) {
    initialProjectId.value = [...selectedProjectIds][0]
    selectedProjectId.value = initialProjectId.value
  }
})

// ---------------------------
//  Computed
// ---------------------------

const ownerId = computed(() => props.selectedJobs[0].hub_id)

const selectedJobIds = computed(() => props.selectedJobs.map((j) => j.id))

const hasChanges = computed(() => {
  return selectedProjectId.value !== initialProjectId.value && !hasProjectLoadingError.value
})

const showHint = computed(() => {
  return !selectedProjectId.value && initialProjectId.value === undefined
})

// ---------------------------
//  Watchers
// ---------------------------

watchEffect(() => {
  if (jobStatusMessage.value !== null) hasJobStatusMessage.value = true
  if (projectStatusMessage.value !== null) hasProjectStatusMessage.value = true
})

// ---------------------------
//  Providers
// ---------------------------

provide('userConfirmation', userConfirmed)
provide('ownerId', ownerId)

// ---------------------------
//  Event Handlers
// ---------------------------

const onUserConfirmation = () => {
  if (!selectedProjectId.value) return

  userConfirmed.value = true
  isSaving.value = true
}

const onProjectCreation = (obj: ProjectSelectionMessage) => {
  projectStatusMessage.value = obj
}

const onProjectSelection = (value: string) => {
  selectedProjectId.value = value
}

const onProjectLoadingError = () => {
  hasProjectLoadingError.value = true
}

const onJobsAddedtoProject = (obj: ProjectSelectionMessage) => {
  jobStatusMessage.value = obj

  emits('updated', {
    message: obj.message,
    updatedJobs: props.selectedJobs,
  } as BulkUpdateResponse)

  userConfirmed.value = false
  isSaving.value = false
  dismiss()
}

const onProjectFormDismissed = () => {
  dismiss()
}

// ---------------------------
//  Methods
// ---------------------------

const clearStatusMessage = () => {
  jobStatusMessage.value = null
  projectStatusMessage.value = null
}

const dismiss = () => emits('dismiss')
</script>

<style scoped></style>
